import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Button,
  createTheme,
  Menu,
  MenuItem,
  MenuList,
  Tab,
  Tabs,
  ThemeProvider,
  type SxProps,
  type TabsActions,
  type Theme,
} from "@mui/material";
import { useCompactScreen } from "@shared/frontend/hooks/use-compact-screen.hook";
import { useMuiMenu } from "@shared/frontend/hooks/use-mui-menu.hook";
import {
  toolbarButtonSx,
  toolbarMenuPaperSx,
} from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/toolbar-button-styles";
import { useEffect, useId, useRef } from "react";
import { useAlert } from "../../../contexts/AlertProvider";
import "./TableToolbarTabs.scss";

const theme = createTheme({
  palette: {
    primary: {
      main: "#00b4d8",
    },
    secondary: {
      main: "#05204C",
    },
  },
});

interface SelectOption<T extends string = string> {
  value: T;
  content: React.ReactNode;
}
interface TableToolbarTabsProps<T extends string = string> {
  options: SelectOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
}

export const TableToolbarTabs = <T extends string = string>(
  props: TableToolbarTabsProps<T>,
) => {
  const { onChange, options, value } = props;
  const placeHolderValue = useId();
  const tabsActionRef = useRef<TabsActions>(null);
  const isCompact = useCompactScreen();
  const [menuButtonRef, menuProps, menuActions] =
    useMuiMenu<HTMLButtonElement>();

  const { isSidebarOpen } = useAlert();

  useEffect(() => {
    const timeout = setTimeout(() => {
      tabsActionRef.current?.updateIndicator();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [isSidebarOpen]);

  function handleChange(_: unknown, nextValue: T) {
    if (onChange) onChange(nextValue);
  }

  const selectedOption = options.find((option) => option.value === value);

  if (isCompact) {
    return (
      <ThemeProvider theme={theme}>
        <Button
          ref={menuButtonRef}
          variant="outlined"
          color="secondary"
          onClick={menuActions.toggle}
          endIcon={<ArrowDropDownIcon />}
          sx={
            [
              toolbarButtonSx,
              { fontWeight: 600, minWidth: "6.5rem" },
            ] as SxProps<Theme>
          }
          aria-haspopup="listbox"
          aria-expanded={menuProps.open ? "true" : undefined}
        >
          {selectedOption?.content ?? "בחר רשימה"}
        </Button>
        <Menu
          disablePortal
          {...menuProps}
          slotProps={{
            paper: { sx: toolbarMenuPaperSx } as { sx?: SxProps<Theme> },
          }}
        >
          <MenuList dense={false}>
            {options.map(({ content, value: optionValue }) => (
              <MenuItem
                key={optionValue}
                selected={optionValue === value}
                onClick={() => {
                  onChange(optionValue);
                  menuActions.hide();
                }}
                sx={{
                  minHeight: "2.5rem",
                  fontWeight: optionValue === value ? 600 : 500,
                  fontSize: "0.875rem",
                }}
              >
                {content}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Tabs
        className="tabs-container"
        classes={{ indicator: "line", flexContainer: "flex-container" }}
        value={
          !options.length && !!value
            ? placeHolderValue
            : options.length && !value
              ? placeHolderValue
              : value
        }
        onChange={handleChange}
        action={tabsActionRef}
      >
        {options.map(({ content, value: v }) => (
          <Tab
            classes={{ textColorPrimary: "tab-content" }}
            key={v}
            value={v}
            label={content}
          />
        ))}
        <Tab className="placeholder" value={placeHolderValue} />
      </Tabs>
    </ThemeProvider>
  );
};
